import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider } from "./auth/AuthContext";
import { CompanyProvider } from "./companies/CompanyContext";
import { useBranding } from "./branding/BrandingContext";
import RequireAuth from "./auth/RequireAuth";
import RequireCompanyPermission from "./auth/RequireCompanyPermission";
import RequireSuperadmin from "./auth/RequireSuperadmin";
import RequireAdminView from "./auth/RequireAdminView";

// Eager geladen: Auth- und Einstiegsseiten (klein, beim ersten Render gebraucht)
import Login from "./pages/Login";
import Home from "./pages/Home";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Register from "./pages/Register";
import RegistrationSuccess from "./pages/RegistrationSuccess";
import AcceptInvitation from "./pages/AcceptInvitation";
import OAuthCallback from "./pages/OAuthCallback";

// Lazy geladen: groessere Dashboard-Bereiche (Charts, Tabellen, Admin)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Reputation = lazy(() => import("./pages/Reputation"));
const Wikipedia = lazy(() => import("./pages/Wikipedia"));
const Standorte = lazy(() => import("./pages/Standorte"));
const Competitor = lazy(() => import("./pages/Competitor"));
const AiOverview = lazy(() => import("./pages/AiOverview"));
const AiVisibility = lazy(() => import("./pages/AiVisibility"));
const ContentDashboard = lazy(() => import("./pages/ContentDashboard"));
const ContentOverview = lazy(() => import("./pages/ContentOverview"));
const MediaLibrary = lazy(() => import("./pages/MediaLibrary"));
const SearchDashboard = lazy(() => import("./pages/SearchDashboard"));
const ChangeHistory = lazy(() => import("./pages/ChangeHistory"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Profile = lazy(() => import("./pages/Profile"));
const Administration = lazy(() => import("./pages/Administration"));
const ReleaseNotes = lazy(() => import("./pages/ReleaseNotes"));
const LegalTerms = lazy(() => import("./pages/LegalTerms"));
const LegalPrivacy = lazy(() => import("./pages/LegalPrivacy"));
const LegalImprint = lazy(() => import("./pages/LegalImprint"));

// Minimales Fallback waehrend Lazy-Chunks laden.
function RouteSuspenseFallback() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: "#666",
      }}
    >
      <div>Laden …</div>
    </div>
  );
}

export default function App() {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();

  useEffect(() => {
    const setMetaContent = (selector, content) => {
      const element = document.querySelector(selector);
      if (element) {
        element.setAttribute("content", content);
      }
    };

    const baseTitle = t("seo.title");
    const appName = branding?.app_name || "DATA optimizer";
    document.title = `${baseTitle} | ${appName}`;
    setMetaContent('meta[name="description"]', t("seo.description"));
    setMetaContent('meta[property="og:title"]', t("seo.ogTitle"));
    setMetaContent('meta[property="og:description"]', t("seo.ogDescription"));
    document.documentElement.lang = i18n.resolvedLanguage?.startsWith("en")
      ? "en"
      : "de";
  }, [i18n.resolvedLanguage, t, branding?.app_name]);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CompanyProvider>
          <Suspense fallback={<RouteSuspenseFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/verify" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register/success" element={<RegistrationSuccess />} />
              <Route path="/invite/:token" element={<AcceptInvitation />} />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route path="/terms-of-use" element={<LegalTerms />} />
              <Route path="/datenschutz" element={<LegalPrivacy />} />
              <Route path="/imprint" element={<LegalImprint />} />

              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/content"
                element={<Navigate to="/content/blog" replace />}
              />
              <Route
                path="/content/overview"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permissions={["can_view_content", "can_view_content_overview"]}>
                      <ContentOverview />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/content/blog"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permissions={["can_view_content", "can_view_content_blog"]}>
                      <ContentDashboard channel="blog" />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/content/linkedin"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permissions={["can_view_content", "can_view_content_linkedin"]}>
                      <ContentDashboard channel="linkedin" />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/content/meta"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permissions={["can_view_content", "can_view_content_meta"]}>
                      <ContentDashboard channel="meta" />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/content/youtube"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permissions={["can_view_content", "can_view_content_youtube"]}>
                      <ContentDashboard channel="youtube" />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/content/press"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permissions={["can_view_content", "can_view_content_press"]}>
                      <ContentDashboard channel="press-release" />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/content/press-release"
                element={<Navigate to="/content/press" replace />}
              />
              <Route
                path="/content/media"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permission="can_view_content">
                      <MediaLibrary />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/search"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permission="can_view_search">
                      <SearchDashboard />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard/reputation"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permission="can_view_reputation">
                      <Reputation />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard/wikipedia"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permission="can_view_wikipedia">
                      <Wikipedia />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard/competitor"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permission="can_view_competitor">
                      <Competitor />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard/google-ai-visibility"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permission="can_view_ai_overview">
                      <AiOverview />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard/ai-overview"
                element={<Navigate to="/dashboard/google-ai-visibility" replace />}
              />
              <Route
                path="/dashboard/chatgpt-visibility"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permission="can_view_ai_visibility">
                      <AiVisibility />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard/ai-visibility"
                element={<Navigate to="/dashboard/chatgpt-visibility" replace />}
              />
              <Route
                path="/dashboard/locations"
                element={
                  <RequireAuth>
                    <RequireCompanyPermission permission="can_view_locations">
                      <Standorte />
                    </RequireCompanyPermission>
                  </RequireAuth>
                }
              />
              {/* Legacy-Pfad: leitet alte Bookmarks weiter. */}
              <Route
                path="/dashboard/standorte"
                element={<Navigate to="/dashboard/locations" replace />}
              />
              <Route
                path="/profile"
                element={
                  <RequireAuth>
                    <Profile />
                  </RequireAuth>
                }
              />
              <Route
                path="/tasks"
                element={
                  <RequireAuth>
                    <Tasks />
                  </RequireAuth>
                }
              />
              <Route
                path="/administration"
                element={
                  <RequireAuth>
                    <RequireSuperadmin>
                      <Administration />
                    </RequireSuperadmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/changehistory"
                element={
                  <RequireAuth>
                    <RequireAdminView>
                      <ChangeHistory />
                    </RequireAdminView>
                  </RequireAuth>
                }
              />
              <Route
                path="/dashboard/release-notes"
                element={
                  <RequireAuth>
                    <ReleaseNotes />
                  </RequireAuth>
                }
              />
            </Routes>
          </Suspense>
        </CompanyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
